import {
  COMMAND_BUFFER_SIZE,
  COMMAND_HEADER_BYTES,
  COMMAND_RECORD_BYTES,
  COMMAND_RING_CAPACITY,
  COMMAND_TUNING_SLOTS,
  CommandHeaderOffset,
  CommandRecordOffset,
  commandRecordByteOffset,
  type StateBuffer,
} from './layout';

export type CommandMode = 'acro' | 'angle' | 'manual' | 'auto';

export interface Command {
  roll: number;
  pitch: number;
  yaw: number;
  throttle: number;
  mode: CommandMode;
  arm: boolean;
  disarm: boolean;
  reset: boolean;
  releaseManual: boolean;
  tuning: number[];
}

const COMMAND_MODES = ['acro', 'angle', 'manual', 'auto'] as const satisfies readonly CommandMode[];
const CommandFlag = Object.freeze({
  arm: 1 << 0,
  disarm: 1 << 1,
  reset: 1 << 2,
  releaseManual: 1 << 3,
});

const commandFlags = (command: Command): number =>
  (command.arm ? CommandFlag.arm : 0) |
  (command.disarm ? CommandFlag.disarm : 0) |
  (command.reset ? CommandFlag.reset : 0) |
  (command.releaseManual ? CommandFlag.releaseManual : 0);

const recordSequence = (logicalIndex: number): number => ((logicalIndex + 1) * 2) | 0;

/**
 * Lock-free single-producer/single-consumer command queue.
 *
 * Its full-ring rule is latest-wins: before publishing a new command, the
 * producer atomically advances the tail enough to discard the oldest command.
 * Per-record sequence stamps prevent a consumer from returning a slot while it
 * is being reused after an overwrite.
 */
export class CommandRing {
  private readonly buffer: StateBuffer;
  private readonly header: Int32Array;

  constructor(buffer: StateBuffer) {
    if (buffer.byteLength !== COMMAND_BUFFER_SIZE) {
      throw new RangeError(`command buffer must be exactly ${COMMAND_BUFFER_SIZE} bytes`);
    }
    this.buffer = buffer;
    this.header = new Int32Array(buffer, 0, COMMAND_HEADER_BYTES / 4);
  }

  push(command: Command): void {
    const headIndex = CommandHeaderOffset.head / 4;
    const head = Atomics.load(this.header, headIndex);
    const minimumTail = head - COMMAND_RING_CAPACITY + 1;
    if (minimumTail > 0) this.advanceTailAtLeast(minimumTail);

    const slot = ((head % COMMAND_RING_CAPACITY) + COMMAND_RING_CAPACITY) % COMMAND_RING_CAPACITY;
    const byteOffset = commandRecordByteOffset(slot);
    const sequence = new Int32Array(this.buffer, byteOffset, 1);
    const committedSequence = recordSequence(head);
    Atomics.store(sequence, 0, committedSequence - 1);
    const view = new DataView(this.buffer, byteOffset, COMMAND_RECORD_BYTES);
    const channels = [command.roll, command.pitch, command.yaw, command.throttle];
    channels.forEach((value, index) => {
      view.setFloat64(CommandRecordOffset.channels + index * 8, value, true);
    });
    const mode = COMMAND_MODES.indexOf(command.mode);
    if (mode < 0) throw new RangeError(`unsupported command mode: ${command.mode}`);
    view.setInt32(CommandRecordOffset.mode, mode, true);
    view.setUint32(CommandRecordOffset.flags, commandFlags(command), true);
    for (let index = 0; index < COMMAND_TUNING_SLOTS; index += 1) {
      view.setFloat64(CommandRecordOffset.tuning + index * 8, command.tuning[index] ?? 0, true);
    }
    Atomics.store(sequence, 0, committedSequence);
    Atomics.store(this.header, headIndex, head + 1);
  }

  drain(): Command[] {
    const commands: Command[] = [];
    let command: Command | null;
    while ((command = this.pop()) !== null) commands.push(command);
    return commands;
  }

  pop(): Command | null {
    const headIndex = CommandHeaderOffset.head / 4;
    const tailIndex = CommandHeaderOffset.tail / 4;
    while (true) {
      const tail = Atomics.load(this.header, tailIndex);
      const head = Atomics.load(this.header, headIndex);
      if (tail >= head) return null;

      const slot = ((tail % COMMAND_RING_CAPACITY) + COMMAND_RING_CAPACITY) % COMMAND_RING_CAPACITY;
      const byteOffset = commandRecordByteOffset(slot);
      const sequence = new Int32Array(this.buffer, byteOffset, 1);
      const expectedSequence = recordSequence(tail);
      const before = Atomics.load(sequence, 0);
      if (before !== expectedSequence || (before & 1) !== 0) continue;

      const local = new ArrayBuffer(COMMAND_RECORD_BYTES);
      new Uint8Array(local).set(new Uint8Array(this.buffer, byteOffset, COMMAND_RECORD_BYTES));
      const after = Atomics.load(sequence, 0);
      if (before !== after) continue;
      if (Atomics.compareExchange(this.header, tailIndex, tail, tail + 1) !== tail) continue;
      return this.decode(new DataView(local));
    }
  }

  private advanceTailAtLeast(minimum: number): void {
    const tailIndex = CommandHeaderOffset.tail / 4;
    while (true) {
      const tail = Atomics.load(this.header, tailIndex);
      if (tail >= minimum) return;
      if (Atomics.compareExchange(this.header, tailIndex, tail, minimum) === tail) return;
    }
  }

  private decode(view: DataView): Command {
    const modeIndex = view.getInt32(CommandRecordOffset.mode, true);
    const mode = COMMAND_MODES[modeIndex];
    if (mode === undefined) throw new RangeError(`corrupt command mode: ${modeIndex}`);
    const flags = view.getUint32(CommandRecordOffset.flags, true);
    return {
      roll: view.getFloat64(CommandRecordOffset.channels, true),
      pitch: view.getFloat64(CommandRecordOffset.channels + 8, true),
      yaw: view.getFloat64(CommandRecordOffset.channels + 16, true),
      throttle: view.getFloat64(CommandRecordOffset.channels + 24, true),
      mode,
      arm: (flags & CommandFlag.arm) !== 0,
      disarm: (flags & CommandFlag.disarm) !== 0,
      reset: (flags & CommandFlag.reset) !== 0,
      releaseManual: (flags & CommandFlag.releaseManual) !== 0,
      tuning: Array.from({ length: COMMAND_TUNING_SLOTS }, (_, index) =>
        view.getFloat64(CommandRecordOffset.tuning + index * 8, true),
      ),
    };
  }
}
